<template>
    <div
        class="fixed right-0 top-0 z-[99] h-screen overflow-auto border-l-2 bg-white"
        :class="{ 'left-24': devPanelIsOpen, 'left-[100vw]': !devPanelIsOpen }"
    >
        <button
            class="fixed top-1/2 z-[99999] w-16 -translate-y-1/2 appearance-none bg-green-500 p-4 text-white"
            :class="{ 'left-8': devPanelIsOpen, 'left-[calc(100vw-4rem)]': !devPanelIsOpen }"
            @click="devPanelIsOpen = !devPanelIsOpen"
        >
            &larr;
        </button>
        <div class="relative flex flex-col gap-8 p-8">
            <div class="text-center">
                <h1 class="text-2xl font-bold">
                    Last sent payloads
                </h1>
                <p>Be aware that the payload values have been sorted alphabetically, to make it easier to compare them.</p>
            </div>
            <div class="grid grid-cols-2 grid-rows-1 gap-8">
                <div class="flex flex-col gap-4 border-2 p-4">
                    <div>
                        <h2>Local calculator payload:</h2>
                        <p>Should receive user input + entire database. Runs every time the local cart changes.</p>
                    </div>
                    <div class="relative">
                        <button class="absolute right-4 top-4 rounded-md bg-black p-2 text-white" @click="copyError(cart().devInfo.localPayload)">
                            Copy
                        </button>
                        <pre class="flex h-52 overflow-x-auto overflow-y-scroll bg-gray-100 p-4">{{ sortObjectProperties(cart().devInfo.localPayload) }}</pre>
                    </div>
                </div>
                <div class="flex flex-col gap-4 border-2 p-4">
                    <div>
                        <h2>Calculator service payload:</h2>
                        <p>Should only receive user input. Runs when a user is idle for 500ms.</p>
                    </div>
                    <div class="relative">
                        <button class="absolute right-4 top-4 rounded-md bg-black p-2 text-white" @click="copyError(cart().devInfo.validatedPayload)">
                            Copy as JSON
                        </button>
                        <button class="absolute right-36 top-4 rounded-md bg-black p-2 text-white" @click="copyGqlObject(cart().devInfo.validatedPayload)">
                            Copy as GQL
                        </button>
                        <pre class="flex h-52 overflow-x-auto overflow-y-scroll bg-gray-100 p-4">{{ sortObjectProperties(cart().devInfo.validatedPayload) }}</pre>
                    </div>
                </div>
            </div>
            <div class="text-center">
                <h1 class="text-2xl font-bold">
                    Last received responses
                </h1>
                <p>Be aware that the response values have been sorted alphabetically, to make it easier to compare them.</p>
                <p>Additionally the logs <code>response._log</code> have been removed from these responses. They can be viewed separately below.</p>
                <p>Lastly, the product with ID <code>-5</code> has been filtered out.</p>
            </div>
            <div class="grid grid-cols-2 grid-rows-1 gap-8">
                <div class="flex flex-col gap-4 border-2 p-4">
                    <h2>Local calculator response:</h2>
                    <div class="relative">
                        <button class="absolute right-4 top-4 rounded-md bg-black p-2 text-white" @click="copyError(cart().cartPricesLocal)">
                            Copy
                        </button>
                        <pre class="flex h-52 overflow-x-auto overflow-y-scroll bg-gray-100 p-4">{{ sortObjectProperties(cart().cartPricesLocal) }}</pre>
                    </div>
                </div>
                <div class="flex flex-col gap-4 border-2 p-4">
                    <h2>Calculator service response:</h2>
                    <div class="relative">
                        <button class="absolute right-4 top-4 rounded-md bg-black p-2 text-white" @click="copyError(cart().cartPricesValidated)">
                            Copy
                        </button>
                        <pre class="flex h-52 overflow-x-auto overflow-y-scroll bg-gray-100 p-4">{{ sortObjectProperties(cart().cartPricesValidated) }}</pre>
                    </div>
                </div>
            </div>
            <div class="text-center">
                <h1 class="text-2xl font-bold">
                    Last received logs
                </h1>
                <p>Be aware that the log values have been sorted alphabetically, to make it easier to compare them.</p>
            </div>
            <div class="grid grid-cols-2 grid-rows-1 gap-8">
                <div class="flex flex-col gap-4 border-2 p-4">
                    <h2>Local calculator log:</h2>
                    <div class="relative">
                        <button class="absolute right-4 top-4 rounded-md bg-black p-2 text-white" @click="copyError(cart().cartPricesLocal)">
                            Copy
                        </button>
                        <pre class="flex h-52 overflow-x-auto overflow-y-scroll bg-gray-100 p-4">{{ sortObjectProperties(cart().devInfo.localLog) }}</pre>
                    </div>
                </div>
                <div class="flex flex-col gap-4 border-2 p-4">
                    <h2>Calculator service log:</h2>
                    <div class="relative">
                        <button class="absolute right-4 top-4 rounded-md bg-black p-2 text-white" @click="copyError(cart().cartPricesValidated)">
                            Copy
                        </button>
                        <pre class="flex h-52 overflow-x-auto overflow-y-scroll bg-gray-100 p-4">{{ sortObjectProperties(cart().devInfo.validatedLog) }}</pre>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { cart } from '~/store/cart'
    const devPanelIsOpen = ref(false)
    function copyError (error: string) {
        navigator.clipboard.writeText(JSON.stringify(sortObjectProperties(error), null, 2))
    }
    function toGraphQLParams (input: any): string {
        let params = ''
        for (const key in input) {
            if (Object.prototype.hasOwnProperty.call(input, key)) {
                const value = input[key]
                if (typeof value === 'string') {
                    params += `${key}: "${value}", `
                } else if (typeof value === 'number' || typeof value === 'boolean') {
                    params += `${key}: ${value}, `
                } else if (Array.isArray(value)) {
                    if (value.length === 0) {
                        params += `${key}: [], `
                    } else {
                        params += `${key}: [`
                        for (const element of value) {
                            if (typeof element === 'object') {
                                params += `{${toGraphQLParams(element)}}, `
                            } else {
                                params += `${element}, `
                            }
                        }
                        params = params.slice(0, -2) // remove trailing comma and space
                        params += '], '
                    }
                } else if (typeof value === 'object') {
                    if (Object.keys(value).length === 0) {
                        params += `${key}: {}, `
                    } else {
                        params += `${key}: {${toGraphQLParams(value)}}, `
                    }
                }
            }
        }
        return params.slice(0, -2) // remove trailing comma and space
    }
    function copyGqlObject (input) {
        navigator.clipboard.writeText(sortObjectProperties(toGraphQLParams({
            input
        })))
    }

    function sortObjectProperties (input: any): any {
        if (typeof input !== 'object' || input === null) {
            return input
        }

        if (Array.isArray(input)) {
            return input.map(sortObjectProperties)
        }

        const sortedObject: any = {}
        const keys = Object.keys(input).sort()

        for (const key of keys) {
            sortedObject[key] = sortObjectProperties(input[key])
        }

        return sortedObject
    }
</script>
